import React, { useEffect, useState } from 'react'
import {
  VisibilityOutlined
} from '@mui/icons-material'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'

import { PaymentAllocation } from './PaymentCollectionSearchForm'

const columnHelper = createColumnHelper<PaymentAllocation>()

export default function PaymentAllocationList({
  data,
  setShowDetailedView,
  onViewDetails // New prop to handle the view details action
}: {
  data: PaymentAllocation[]
  setShowDetailedView: React.Dispatch<React.SetStateAction<boolean>>
  onViewDetails: (allocationId: string, companyName: string) => void // New prop
}) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })

  const columns = [
    columnHelper.accessor('allocation_id', {
      header: 'Allocation ID',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('allocation_date', {
      header: 'Allocation Date',
      cell: (info) => (
        <Typography variant='body2' fontFamily='monospace'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.accessor('company_name', {
      header: 'Company Name', // Fixed typo
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('allocated_amount', {
      header: 'Allocated Amount',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('reference_number', {
      header: 'Reference Number',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('payment_mode', {
      header: 'Payment Mode',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('created_by', {
      header: 'Created By',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('created_on', {
      header: 'Created On',
      cell: (info) => (
        <Typography variant='body2' fontWeight={600}>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const rowData = info.row.original
        return (
          <Tooltip title='View Details' placement='top'>
            <IconButton
              size='small'
              color='primary'
              onClick={() => {
                // Pass the allocation_id and company_name to parent
                onViewDetails(rowData.allocation_id, rowData.company_name)
                setShowDetailedView(true)
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
            >
              <VisibilityOutlined fontSize='small' />
            </IconButton>
          </Tooltip>
        )
      },
      size: 80,
      enableSorting: false
    })
  ]

  const table = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  })

  const { pageIndex, pageSize } = table.getState().pagination

  // Keep page in range if data changes
  const totalRows = table.getPrePaginationRowModel().rows.length
  useEffect(() => {
    const maxPage = Math.max(Math.ceil(totalRows / pageSize) - 1, 0)
    if (pageIndex > maxPage) table.setPageIndex(maxPage)
  }, [pageIndex, pageSize, totalRows, table])

  const paginatedRowCount = table.getFilteredRowModel
    ? table.getFilteredRowModel().rows.length
    : totalRows

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table?.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>No Statement found</Typography>
          </Box>
        )}

        {/* Pagination controls */}
        <TablePagination
          component='div'
          count={paginatedRowCount}
          page={pageIndex}
          onPageChange={(_evt, newPage) => table.setPageIndex(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(evt) => {
            const newSize = parseInt(evt.target.value, 10)
            table.setPageSize(newSize)
            table.setPageIndex(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  )
}
